import { useState, useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import { CustomNode, CustomEdge, HistoryEntry } from '../types';
import { useActionSnackbar } from '../contexts/ActionSnackbarContext'; // Changed to relative path

interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
  canUndo: boolean;
  canRedo: boolean;
}

interface UseHistoryReturn {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  addToHistory: (state: { nodes: CustomNode[]; edges: CustomEdge[] }) => void;
  clearHistory: () => void;
}

const MAX_HISTORY_SIZE = 50;

export function useHistory(): UseHistoryReturn {
  const { setNodes, setEdges } = useReactFlow();
  const { showSnackbar } = useActionSnackbar(); // Initialize snackbar hook
  const [history, setHistory] = useState<HistoryState>({
    past: [],
    future: [],
    canUndo: false,
    canRedo: false,
  });

  const addToHistory = useCallback(
    (state: { nodes: CustomNode[]; edges: CustomEdge[] }) => {
      const entry: HistoryEntry = {
        nodes: state.nodes,
        edges: state.edges,
        timestamp: Date.now(),
      };

      setHistory((prev) => {
        const past = [...prev.past, entry];
        if (past.length > MAX_HISTORY_SIZE) {
          past.shift();
        }

        return {
          past,
          future: [],
          canUndo: true,
          canRedo: false,
        };
      });
    },
    [],
  );

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev;

      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);

      setNodes(previous.nodes);
      setEdges(previous.edges);

      return {
        past: newPast,
        future: [previous, ...prev.future],
        canUndo: newPast.length > 0,
        canRedo: true,
      };
    });
    showSnackbar('Undo'); // Show snackbar
  }, [setNodes, setEdges, showSnackbar]); // Added showSnackbar dependency

  const redo = useCallback(() => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev;

      const next = prev.future[0];
      const newFuture = prev.future.slice(1);

      setNodes(next.nodes);
      setEdges(next.edges);

      return {
        past: [...prev.past, next],
        future: newFuture,
        canUndo: true,
        canRedo: newFuture.length > 0,
      };
    });
    showSnackbar('Redo'); // Show snackbar
  }, [setNodes, setEdges, showSnackbar]); // Added showSnackbar dependency

  const clearHistory = useCallback(() => {
    setHistory({
      past: [],
      future: [],
      canUndo: false,
      canRedo: false,
    });
  }, []);

  return {
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    undo,
    redo,
    addToHistory,
    clearHistory,
  };
}
